import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Notification timestamps are the app's one deliberate exception to ADR-0007's local-to-viewer
// display rule: US-101849 specifies Eastern Time for every notification row and date header.
// ADR-0007 anticipated exactly this ("if a regulator/report later needs a fixed tz, format at
// that boundary only") and requires date logic to live in this module rather than in features —
// so the exception is implemented here, once, and nowhere else. See ADR-0019.

dayjs.extend(utc);
dayjs.extend(timezone);

/** The single fixed timezone in the app. IANA name, so DST is the library's problem, not ours. */
export const EASTERN_TZ = 'America/New_York';

function eastern(iso: string | null | undefined) {
  if (!iso) return null;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.tz(EASTERN_TZ) : null;
}

/**
 * Row time — "09:45".
 *
 * 24-hour and zero-padded because that is what the web design renders (Figma 621:549480). The
 * story's own example is 12-hour ("2:30 PM"), which is the *mobile* row where date and time share
 * one line; on web the date lives in the group header, so only the clock time is shown here.
 */
export function formatEasternTime(iso: string | null | undefined): string | null {
  return eastern(iso)?.format('HH:mm') ?? null;
}

/**
 * The calendar day an instant falls on **in ET**, as `YYYY-MM-DD`.
 *
 * This is the grouping key, so it must be computed in ET too: an event at 23:30 ET belongs to
 * that ET day even though it is already the next day in UTC.
 */
export function easternDateKey(iso: string | null | undefined): string | null {
  return eastern(iso)?.format('YYYY-MM-DD') ?? null;
}

/**
 * Date-group header — "Today", else "Jan 15", else "Jan 15, 2025".
 *
 * The year is appended once the date falls in a different ET year than today. Unread
 * notifications are retained indefinitely (US-101849 § RETENTION), so a bare "Jan 15" would
 * otherwise be ambiguous on a notification that has sat unread across a new year.
 */
export function formatEasternDateHeader(iso: string | null | undefined): string | null {
  const value = eastern(iso);
  if (!value) return null;
  const today = dayjs().tz(EASTERN_TZ);
  if (value.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')) return 'Today';
  return value.year() === today.year() ? value.format('MMM D') : value.format('MMM D, YYYY');
}

/**
 * Absolute date + time in ET — "Oct 12, 2024 | 09:15 AM ET".
 *
 * The History row (Figma 639:589157) prints the whole stamp above the event title, because its
 * list has no date-group headers to carry the day. Alerts, which does group by date, uses
 * {@link formatEasternTime} for the row and {@link formatEasternDateHeader} for the group.
 */
export function formatEasternDateTime(iso: string | null | undefined): string | null {
  return eastern(iso)?.format('MMM D, YYYY | hh:mm A [ET]') ?? null;
}

/** Row time with the zone spelled out — "09:15 AM ET" (Figma 772:632179, Alerts). */
export function formatEasternClock(iso: string | null | undefined): string | null {
  return eastern(iso)?.format('hh:mm A [ET]') ?? null;
}
